import React, { useMemo, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';

import BallotIcon from '@mui/icons-material/Ballot';
import CopyIcon from '@mui/icons-material/FileCopy';
// import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { DEFAULT_EXERCISE_TYPE } from '@ts-core/utils/Constants';
import Dialog, { useDialog } from '@ts-core/components/Dialog';
import SpeedDialMenu from '@ts-core/components/SpeedDialMenu';

import { ExerciseType } from '@ts-core/modules/exercises/types/index';

import ImportExercisesFromCourse from '../../components/ImportExercisesFromCourse';
// import UploadExercisesFromCSV from '../../components/UploadExercisesFromCSV/UploadExercisesFromCSV';
import ExerciseTypeSelector, { useExerciseTypeSelectorOptions } from '../../components/ExerciseTypeSelector';
import ExercisesTable from '../../components/ExercisesTable';

import { useExerciseActions, useExercises } from '../../hooks';
import { Exercise } from '../../types';

const ExercisesPage = () => {
  const intl = useIntl();
  const history = useHistory();

  const { courseId, subjectId } = useParams<{ courseId: string; subjectId: string }>();

  const {
    onOpenDialog: onOpenImportExercisesDialog,
    onCloseDialog: onCloseImportExercisesDialog,
    dialogProps: importExercisesDialogProps,
  } = useDialog();

  const { exercises, loading } = useExercises({ courseId, subjectId });
  const { onToggleExercisePractice, onRemoveExercise, onSortExercises, onImportExercises, importExercisesLoading } = useExerciseActions({
    courseId,
    subjectId,
  });

  const exerciseTypeOptions = useExerciseTypeSelectorOptions();

  const handleImportExercises = useCallback(
    (exercises: Exercise[]) => {
      onImportExercises(exercises, onCloseImportExercisesDialog);
    },
    [onImportExercises, onCloseImportExercisesDialog]
  );

  /**
   * Navigate to exercise
   */
  const handleSelectExercise = useCallback(
    (exercise: Exercise) => {
      history.push(`exercises/exercise/${exercise.type || DEFAULT_EXERCISE_TYPE}/${exercise.id}`);
    },
    [history]
  );

  /**
   * Add new exercise
   */
  const {
    onOpenDialog: onOpenSelectExerciseTypeDialog,
    onCloseDialog: onCloseSelectExerciseTypeDialog,
    dialogProps: selectExerciseTypeDialogProps,
  } = useDialog();
  const handleSelectExerciseType = useCallback(
    (exerciseTye: ExerciseType) => {
      onCloseSelectExerciseTypeDialog();
      history.push(`exercises/exercise/${exerciseTye}`);
    },
    [history, onCloseSelectExerciseTypeDialog]
  );
  const handleOpenSelectExerciseTypeDialog = useCallback(() => {
    onOpenSelectExerciseTypeDialog({
      title: intl.formatMessage({ id: 'exercise.types.pick' }),
    });
  }, [intl, onOpenSelectExerciseTypeDialog]);

  /**
   * Import exercises from course
   */
  const handleOpenImportExercisesFromCourse = useCallback(() => {
    onOpenImportExercisesDialog({
      title: intl.formatMessage({ id: 'button.copyExercises.title' }),
    });
  }, [intl, onOpenImportExercisesDialog]);

  /**
   * Import exercises from csv file
   */
  // const {
  //   onOpenDialog: onOpenImportExercisesFromCsvDialog,
  //   onCloseDialog: onCloseImportExercisesFromCsvDialog,
  //   dialogProps: importExercisesFromCsvDialogProps,
  // } = useDialog();

  // const handleOpenImportExercisesFromCsv = useCallback(() => {
  //   onOpenImportExercisesFromCsvDialog({
  //     title: intl.formatMessage({ id: 'button.uploadExercisesFromCsv.title' }),
  //   });
  // }, [intl, onOpenImportExercisesFromCsvDialog]);

  /**
   * Menu Options
   */
  const speedDialOptions = useMemo(
    () => [
      {
        icon: <BallotIcon />,
        title: intl.formatMessage({ id: 'button.addExercise.title' }),
        onClick: handleOpenSelectExerciseTypeDialog,
      },
      {
        icon: <CopyIcon />,
        title: intl.formatMessage({ id: 'button.copyExercises.title' }),
        onClick: handleOpenImportExercisesFromCourse,
      },
    ],
    [intl, handleOpenSelectExerciseTypeDialog, handleOpenImportExercisesFromCourse]
  );

  return (
    <>
      <ExercisesTable
        exercises={exercises}
        loading={loading}
        onRemoveExercise={onRemoveExercise}
        onEditExercise={handleSelectExercise}
        onTogglePractice={onToggleExercisePractice}
        onSortExercises={onSortExercises}
      />
      {/* <Dialog {...importExercisesFromCsvDialogProps}>
        <UploadExercisesFromCSV onCloseDialog={onCloseImportExercisesFromCsvDialog} />
      </Dialog> */}
      <Dialog {...importExercisesDialogProps}>
        <ImportExercisesFromCourse onImportExercises={handleImportExercises} importingExercises={importExercisesLoading} />
      </Dialog>
      <Dialog {...selectExerciseTypeDialogProps}>
        <ExerciseTypeSelector options={exerciseTypeOptions} onSelectExerciseType={handleSelectExerciseType} />
      </Dialog>
      <SpeedDialMenu options={speedDialOptions} />
    </>
  );
};

export default ExercisesPage;
